import {
  IDropdownOptionV2,
  ISystemBase,
  ISystemStory,
  ISystemStoryVersion,
  ISystemSubtype,
  generateStoryLink as sharedGenerateStoryLink,
  SUBTYPES,
  VALUES,
} from "@dl/declarations";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import { FieldErrors } from "react-hook-form";
import * as yup from "yup";

dayjs.extend(utc);

export const mapLookupsToOptions = (
  lookups: any[],
  labelKey: string,
  groupKey: (item: any) => string,
): (IDropdownOptionV2 & { groupKey: string })[] => {
  return lookups.map((item) => ({
    ...item,
    _id: item._id,
    slug: String(item.slug),
    label: String(item[labelKey as keyof typeof item]),
    groupKey: groupKey(item),
  }));
};

export const generateStoryLink = (
  story: Parameters<typeof sharedGenerateStoryLink>[0],
  addDomain = true,
  addSlug = true,
  type: "use_url" | "use_segments" = "use_url",
) => {
  return sharedGenerateStoryLink(story, {
    type,
    domain: {
      add: addDomain,
      url: import.meta.env.VITE_FRONTEND_BASE_URL,
    },
    slug: {
      add: addSlug,
    },
  });
};

export const renderSubtypeLabel = (
  subtype: ISystemSubtype,
  field: "NAME" | "TITLE" | "SHORT" = "TITLE",
) => {
  if (!subtype) {
    return VALUES.EMPTY_FIELD;
  }

  if (typeof subtype === "string") {
    return VALUES.EMPTY_FIELD;
  }

  const subtypes = [SUBTYPES.NEWS, SUBTYPES.RESEARCH, SUBTYPES.SYSTEM];

  type TSubtypeSlug = (typeof subtypes)[number]["SLUG"];

  const index = subtypes.findIndex(
    (x) => x.SLUG === (subtype.slug as TSubtypeSlug),
  );

  if (index === -1) {
    return subtype?.name || VALUES.EMPTY_FIELD;
  }

  return subtypes[index][field];
};

export const generateSearchParam = (
  basePath: string,
  key: string,
  value?: string | number,
) => {
  let searchParam = `_${basePath.replace("/", "-")}_${key}`;

  if (value) {
    searchParam += `=${value}`;
  }

  return searchParam;
};

export const generateFormErrorsTooltip = <T extends yup.AnyObjectSchema>(
  errors: FieldErrors<T> = {},
  prefix: string = "Form fields are invalid",
) => {
  let tooltip = prefix;

  if (Object.values(errors).length === 0) {
    return tooltip;
  }

  if (Object.values(errors).length > 0) {
    tooltip +=
      ":<br/>" +
      Object.values(errors)
        .filter((error) => Boolean(error))
        .map((error) => error?.message)
        .join("<br/>");
  }

  return tooltip;
};

export const mapEntityToId = <T extends string | ISystemBase>(entity: T) => {
  return (typeof entity === "string" ? entity : entity?._id) || "";
};

export const mapEntitiesArrayToIdArray = <T extends string[] | ISystemBase[]>(
  entities: T,
) => {
  if (Array.isArray(entities) && entities.length > 0) {
    return entities.map((item) => mapEntityToId(item));
  }

  return [];
};

export const getEditorStoryVersion = (story: ISystemStory) => {
  const {
    _id,
    url,
    slug,
    previous_urls,

    is_locked,
    locked_at,
    locked_by,
    lockedByObject,

    published_story_version,
    publishedStoryVersionObject,
    saved_story_version,
    savedStoryVersionObject,

    scheduleObject,
    collectionsObjects,

    workflow: story_workflow,

    created_at: story_created_at,
    updated_at: story_updated_at,
    publish_date: story_publish_date,
    first_publish_date: story_first_publish_date,
    display_date: story_display_date,

    idempotency_key
  } = story;

  const usedVersion = { ...(story || {}), ...(savedStoryVersionObject || {}) };

  const {
    headline,
    subheadline,
    description,
    meta_data,

    guests,
    sub_vertical,
    content_elements,

    subtype,
    subtypeObject,
    primary_vertical,
    primaryVerticalObject,

    tags,
    tagsObjects,
    verticals,
    verticalsObjects,
    authors,
    authorsObjects,

    main_asset,
    mainAssetObject,
    main_asset_info,
    homepage_asset,
    homepageAssetObject,
    social_media_asset,
    socialMediaAssetObject,

    workflow: story_version_workflow,
    version: story_version_version,

    created_at: story_version_created_at,
    updated_at: story_version_updated_at,
  } = usedVersion;

  return {
    headline: headline || "",
    subheadline: subheadline || "",
    description: description || "",
    meta_data: meta_data || {
      title: "",
      description: "",
    },
    sub_vertical: sub_vertical || "internal",
    guests: guests || [],
    content_elements: content_elements || [],

    subtype: subtype || null,
    subtypeObject: subtypeObject || null,
    primary_vertical: primary_vertical || null,
    primaryVerticalObject: primaryVerticalObject || null,
    tags: tags || [],
    tagsObjects: tagsObjects || [],
    verticals: verticals || [],
    verticalsObjects: verticalsObjects || [],
    authors: authors || [],
    authorsObjects: authorsObjects || [],

    main_asset: main_asset || null,
    mainAssetObject: mainAssetObject || null,
    main_asset_info: main_asset_info || undefined,
    homepage_asset: homepage_asset || null,
    homepageAssetObject: homepageAssetObject || null,
    social_media_asset: social_media_asset || null,
    socialMediaAssetObject: socialMediaAssetObject || null,

    story_created_at,
    story_updated_at,
    story_publish_date,
    story_first_publish_date,
    story_display_date,
    story_version_created_at,
    story_version_updated_at,
    story_version_version,

    _id,
    url,
    slug,
    previous_urls,

    is_locked,
    locked_at,
    locked_by,
    lockedByObject,

    story_workflow,
    story_version_workflow,

    published_story_version,
    publishedStoryVersionObject,
    saved_story_version,
    savedStoryVersionObject,

    scheduleObject,
    collectionsObjects: collectionsObjects || [],

    idempotency_key
  };
};

export const renderDate = (date: string | Date, version: "0" | "1" = "0") => {
  if (!date) return VALUES.EMPTY_FIELD;

  if (version === "1") {
    return dayjs(date).format("MMMM D, YYYY");
  }

  return dayjs(date).format("DD/MM/YYYY");
};

export const renderDateTime = (
  date: string | Date,
  version: "0" | "1" | "2" | "3" | "4" | "5" = "0",
) => {
  if (!date) return VALUES.EMPTY_FIELD;

  if (version === "5") {
    return dayjs(date).format("D/M/YY [-] h.mm A");
  }

  if (version === "4") {
    return dayjs(date).format("DD/MM/YY [at] h:mm A");
  }

  if (version === "3") {
    return dayjs(date).format("DD/MM/YY h:mm");
  }

  if (version === "2") {
    return dayjs(date).format("DD/MM/YYYY,h:mm");
  }

  if (version === "1") {
    return dayjs(date).format("MMMM D, YYYY [at] h:mm A");
  }

  return dayjs(date).format("DD/MM/YYYY h:mm a");
};

export const renderTime = (date: string | Date) => {
  if (!date) return VALUES.EMPTY_FIELD;

  return dayjs(date).format("h:mm A");
};

export const renderVersionInfo = (
  version: ISystemStoryVersion | null,
  publishedVersionId: string | null,
) => {
  if (!version) return VALUES.EMPTY_FIELD;

  let label = `<b>Version ${version.version}</b>`;

  label += `   ${renderDateTime(version.created_at)} - Status:`;

  if (publishedVersionId === version._id) {
    label += "   <b>Currently published</b>";
  } else {
    label += `   <b>${version.workflow || ""}</b>`;
  }

  if (version.created_by) {
    label += `   by <b>${version.createdByObject?.name || VALUES.EMPTY_FIELD}</b>`;
  }

  return label;
};

export const eventHasAttribute = (
  event: Event,
  attributeName: string,
  attributeValues: string[],
): boolean => {
  let el = event.target as HTMLElement | null;

  while (el) {
    if (
      el.hasAttribute(attributeName) &&
      attributeValues.includes(el.getAttribute(attributeName) || "")
    ) {
      return true;
    }

    el = el.parentElement;
  }

  return false;
};
